// Colour tones for action-state and eligibility chips, as theme token pairs
// (no hardcoded colours). Token paths mirror the ones NorthStarClaims uses, so
// they're known-valid against the KareHero palette.

import type {
  ActionState,
  EligibilityStatus,
} from '@/benefits-flow/engine/types';

export type Tone = { bg: string; color: string; border?: string };

// Text on the lime tint is charcoal, not a darker step of lime. The designs app
// swaps in a preview palette where the whole lime family is collapsed to one
// value (palettePreview.ts `flat`), so limeGreen[100] and limeGreen[800] resolve
// to the same hex and lime-on-lime is invisible. Charcoal reads on both the
// collapsed value and llama's pale limeGreen[100], so it is correct under either.
//
// Keyed by the tone field in BenefitsFlow.content.ts ACTIONS.
export const ACTION_TONE: Record<string, Tone> = {
  ready: { bg: 'custom.limeGreen.100', color: 'custom.charcoal' },
  wait: { bg: 'sunflower.main', color: 'custom.charcoal' },
  guide: { bg: 'custom.coral.50', color: 'custom.coral.700' },
  check: { bg: 'custom.marineGreen.50', color: 'custom.charcoal' },
  info: { bg: 'custom.neutral.100', color: 'text.secondary' },
  unknown: {
    bg: 'background.paper',
    color: 'text.secondary',
    border: 'custom.neutral.200',
  },
};

export const STATUS_TONE: Record<EligibilityStatus, Tone> = {
  eligible: { bg: 'custom.limeGreen.100', color: 'custom.charcoal' },
  ineligible: {
    bg: 'background.paper',
    color: 'text.secondary',
    border: 'custom.neutral.200',
  },
  'more-info-needed': { bg: 'sunflower.main', color: 'custom.charcoal' },
};

// Convenience: action id → tone, via the ACTIONS content tone field.
export const actionTone = (tone: string): Tone =>
  ACTION_TONE[tone] ?? ACTION_TONE.info;

// Not exported for runtime use, but documents that these two enums are what the
// tone maps cover.
export type _Covered = ActionState | EligibilityStatus;
